import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

const React = window.EcommerceCore.vendors.React;
const { useState, useEffect, useMemo } = React;

const Icon = window.EcommerceCore.common.components.Icon;
const LightboxModule = window.EcommerceCore.vendors.Lightbox;
const LightboxComponent = LightboxModule?.default || LightboxModule;
const ZoomPlugin = LightboxModule?.Zoom;
const ThumbnailsPlugin = LightboxModule?.Thumbnails;

interface MtcProductGalleryProps {
  product: any;
  selectedVariation: any;
}

export const MtcProductGallery: React.FC<MtcProductGalleryProps> = ({
  product,
  selectedVariation,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [zoomStyle, setZoomStyle] = useState<React.CSSProperties>({
    transform: "scale(1)",
    transformOrigin: "center center",
  });
  const [isHovered, setIsHovered] = useState(false);

  // Collect and deduplicate product & variation images
  const images = useMemo(() => {
    const list: any[] = [];
    const seenKeys = new Set<string | number>();

    const addImg = (img: any) => {
      if (!img) return;
      const key = img.id || img.src;
      if (key && !seenKeys.has(key)) {
        seenKeys.add(key);
        list.push(img);
      }
    };

    if (selectedVariation?.mainImage) addImg(selectedVariation.mainImage);
    if (selectedVariation?.galleryImages) {
      selectedVariation.galleryImages.forEach(addImg);
    }
    if (product?.mainImage) addImg(product.mainImage);
    if (product?.galleryImages) {
      product.galleryImages.forEach(addImg);
    }

    return list;
  }, [product, selectedVariation]);

  // Reset active image index when variation selection changes
  useEffect(() => {
    setActiveIndex(0);
  }, [selectedVariation]);

  const safeActiveIndex = activeIndex >= images.length ? 0 : activeIndex;
  const currentImage = images[safeActiveIndex];

  // Mouse movement tracking for glass magnifier zoom
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const { left, top, width, height } =
      e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomStyle({
      transformOrigin: `${x}% ${y}%`,
      transform: "scale(2.2)",
    });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setZoomStyle({
      transform: "scale(1)",
      transformOrigin: "center center",
    });
  };

  const slides = useMemo(() => {
    return images.map((img) => ({
      src: img.src,
      alt: img.alt || product?.name || "",
    }));
  }, [images, product]);

  const plugins = useMemo(() => {
    const list = [];
    if (ZoomPlugin) list.push(ZoomPlugin);
    if (ThumbnailsPlugin) list.push(ThumbnailsPlugin);
    return list;
  }, []);

  if (images.length === 0) {
    return (
      <div className="mtc-product-gallery">
        <div className="mtc-gallery-main mtc-gallery-main--placeholder">
          <span>Görsel Yok</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mtc-product-gallery">
      {/* Main Image Stage */}
      <div
        className="mtc-gallery-main"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => setLightboxIndex(safeActiveIndex)}
      >
        <img
          src={currentImage?.src || currentImage?.thumbnail}
          alt={currentImage?.alt || product?.name || ""}
          className={`mtc-gallery-main__img ${isHovered ? "is-zoomed" : ""}`}
          style={isHovered ? zoomStyle : undefined}
        />

        {/* Expand / Lightbox Button */}
        <button
          type="button"
          className="mtc-gallery-main__expand-btn"
          onClick={(e) => {
            e.stopPropagation();
            setLightboxIndex(safeActiveIndex);
          }}
          aria-label="Görseli Büyüt"
          title="Görseli Büyüt"
        >
          <Icon iconName="fullscreen-outlined" className="icon--stroke-thick" />
        </button>
      </div>

      {/* Thumbnails Row */}
      {images.length > 1 && (
        <div className="mtc-gallery-thumbnails">
          {images.map((img, idx) => {
            const isActive = idx === safeActiveIndex;
            return (
              <button
                key={img.id || idx}
                type="button"
                className={`mtc-gallery-thumb ${isActive ? "is-active" : ""}`}
                onClick={() => setActiveIndex(idx)}
              >
                <img
                  src={img.thumbnail || img.src}
                  alt={img.alt || `${product?.name || ""} - ${idx + 1}`}
                />
              </button>
            );
          })}
        </div>
      )}

      {/* Lightbox Modal */}
      {LightboxComponent && lightboxIndex !== null && (
        <LightboxComponent
          open={lightboxIndex !== null}
          close={() => setLightboxIndex(null)}
          index={lightboxIndex}
          slides={slides}
          plugins={plugins}
        />
      )}
    </div>
  );
};
